import { useState } from "react";
import { TableCell, TableHead, TableHeader, TableBody } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Eye, HelpCircle, MoreVertical, RefreshCw, CalendarDays, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  NestedDrawerContainer,
  NestedDrawerTableHeaderRow,
  NestedDrawerTableBodyRow,
} from "@/components/shared/NestedDrawerTable";
import { ExpandCollapseToggle } from "@/components/shared/ExpandCollapseToggle";
import { SortableTableHead } from "@/components/shared/SortableTableHead";
import { ConfirmDeleteDialog } from "@/components/shared/ConfirmDeleteDialog";
import { toast } from "@/hooks/use-toast";
import { LandscapeItem } from "@/types";
import { landscapeApi } from "@/resources/landscape";
import { useAppData } from "@/contexts/AppDataContext";
import {
  isLandscapeRunOpenable,
  landscapeRunKeywordCountDisplay,
  runStatusLabel,
  failedLandscapeReason,
} from "@/utils/landscapeRunStatus";
import { PresenceCompare } from "../PresenceCompare";
import { ScheduleLandscapeModal } from "../ScheduleLandscapeModal";
import { Table } from "@/components/ui/table";

const INITIAL_VISIBLE_VERSIONS = 5;

export type SortField = "phrase" | "top4" | "page1" | "keywords" | "date";
export type SortDirection = "asc" | "desc" | null;

export interface NestedSortState {
  field: SortField | null;
  direction: SortDirection;
}

export interface SingleHistoryRowProps {
  instance: LandscapeItem;
  landscapeName: string;
  heroAsin?: string;
  heroProductId?: number;
  onView: (instanceId: string) => void;
  readOnly?: boolean;
}

export function SingleHistoryRow({
  instance,
  landscapeName,
  heroAsin,
  heroProductId,
  onView,
  readOnly,
}: SingleHistoryRowProps) {
  const isOpenable = isLandscapeRunOpenable(instance.status);
  const isStatusUnknown = instance.status === null;
  const { selectedBrandId } = useAppData();
  const { mutate: createLandscape } = landscapeApi.useCreateLandscape();
  const { mutate: deleteLandscape } = landscapeApi.useDeleteLandscape();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);

  const handleClick = () => {
    if (isOpenable) {
      onView(instance.id);
    } else {
      toast({
        title: `Status: ${runStatusLabel(instance.status)}`,
        description:
          (instance.status === "failed" ? failedLandscapeReason(instance.status_info) : instance.status_info) ||
          "No additional info available.",
      });
    }
  };

  return (
    <>
      <NestedDrawerTableBodyRow
        className="cursor-pointer"
        onClick={handleClick}
      >
        <TableCell className="px-3 py-2 w-10" />
        <TableCell className="px-3 py-2 w-8" />
        <TableCell className="px-3 py-2 text-xs">
          <span className="text-foreground">↳ {instance.date}</span>
          {isStatusUnknown && (
            <span className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
              <HelpCircle className="h-3 w-3 shrink-0" />
              Unknown
            </span>
          )}
        </TableCell>
        <TableCell className="px-3 py-2 text-xs">
          <PresenceCompare hero={instance.top4Hero} competitorAvg={instance.top4CompetitorAvg} />
        </TableCell>
        <TableCell className="px-3 py-2 text-xs">
          <PresenceCompare hero={instance.page1Hero} competitorAvg={instance.page1CompetitorAvg} />
        </TableCell>
        <TableCell className="px-3 py-2 text-xs tabular-nums text-center">
          {landscapeRunKeywordCountDisplay(instance.status, instance.keywordCount)}
        </TableCell>
        <TableCell className="px-3 py-2 text-xs tabular-nums whitespace-nowrap">{instance.date}</TableCell>
        <TableCell className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-1">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={handleClick}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{isOpenable ? "View landscape" : "View landscape status"}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {!readOnly ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-popover z-50 rounded-[8px]">
                  <DropdownMenuItem
                    onClick={() =>
                      heroAsin &&
                      selectedBrandId &&
                      landscapeName &&
                      createLandscape({
                        search_phrase: landscapeName,
                        hero_asin: heroAsin,
                        brand_id: selectedBrandId,
                      })
                    }
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setScheduleOpen(true)}>
                    <CalendarDays className="h-4 w-4 mr-2" />
                    Schedule
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onClick={() => setDeleteOpen(true)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
            {!readOnly ? (
              <ConfirmDeleteDialog
                open={deleteOpen}
                onOpenChange={setDeleteOpen}
                title={`Delete "${landscapeName}" version from ${instance.date}?`}
                description="This will permanently delete this version of the landscape. This action cannot be undone."
                onConfirm={() =>
                  deleteLandscape({ landscapeId: Number(instance.id), heroProductId })
                }
              />
            ) : null}
          </div>
        </TableCell>
      </NestedDrawerTableBodyRow>
      <ScheduleLandscapeModal
        open={scheduleOpen}
        onOpenChange={setScheduleOpen}
        landscapeId={Number(instance.id)}
        landscapeName={landscapeName}
      />
    </>
  );
}

interface NestedHistoryTableProps {
  visibleHistory: LandscapeItem[];
  totalHistory: number;
  hasMore: boolean;
  instanceCount: number;
  landscapeName: string;
  heroAsin?: string;
  heroProductId?: number;
  nestedSort: NestedSortState;
  onSort: (field: SortField) => void;
  onViewInstance: (id: string) => void;
  onToggleShowMore: () => void;
  readOnly?: boolean;
}

export function NestedHistoryTable({
  visibleHistory,
  totalHistory,
  hasMore,
  instanceCount,
  landscapeName,
  heroAsin,
  heroProductId,
  nestedSort,
  onSort,
  onViewInstance,
  onToggleShowMore,
  readOnly,
}: NestedHistoryTableProps) {
  return (
    <NestedDrawerContainer>
      <Table>
        <TableHeader>
          <NestedDrawerTableHeaderRow>
            <TableHead className="px-3 py-2 w-10" />
            <TableHead className="px-3 py-2 w-8" />
            <SortableTableHead
              field="date"
              activeField={nestedSort.field}
              direction={nestedSort.direction ?? "asc"}
              onSort={onSort}
              className="px-3 py-2 text-xs font-bold"
            >
              Version
            </SortableTableHead>
            <SortableTableHead
              field="top4"
              activeField={nestedSort.field}
              direction={nestedSort.direction ?? "asc"}
              onSort={onSort}
              className="px-3 py-2 text-xs w-[120px] font-bold"
            >
              Top 4
            </SortableTableHead>
            <SortableTableHead
              field="page1"
              activeField={nestedSort.field}
              direction={nestedSort.direction ?? "asc"}
              onSort={onSort}
              className="px-3 py-2 text-xs w-[120px] font-bold"
            >
              Page 1
            </SortableTableHead>
            <SortableTableHead
              field="keywords"
              activeField={nestedSort.field}
              direction={nestedSort.direction ?? "asc"}
              onSort={onSort}
              className="px-3 py-2 text-xs w-[100px] text-center font-bold"
            >
              Keywords
            </SortableTableHead>
            <SortableTableHead
              field="date"
              activeField={nestedSort.field}
              direction={nestedSort.direction ?? "asc"}
              onSort={onSort}
              className="px-3 py-2 text-xs w-[120px] font-bold"
            >
              Date
            </SortableTableHead>
            <TableHead className="px-3 py-2 text-xs w-[100px] font-bold">Actions</TableHead>
          </NestedDrawerTableHeaderRow>
        </TableHeader>
        <TableBody>
          {visibleHistory.map((instance) => (
            <SingleHistoryRow
              key={instance.id}
              instance={instance}
              landscapeName={landscapeName}
              heroAsin={heroAsin}
              heroProductId={heroProductId}
              onView={onViewInstance}
              readOnly={readOnly}
            />
          ))}
        </TableBody>
      </Table>
      {totalHistory > INITIAL_VISIBLE_VERSIONS && (
        <div className="mt-2 px-4">
          <ExpandCollapseToggle
            expanded={!hasMore}
            expandLabel={`View all ${instanceCount} versions`}
            collapseLabel="Show fewer"
            onToggle={onToggleShowMore}
          />
        </div>
      )}
    </NestedDrawerContainer>
  );
}

export { INITIAL_VISIBLE_VERSIONS };
